import { cn } from "@apex/shared";
import { useWidget } from "../../state";
import { FilterBar } from "../FilterBar";
import { CatalogItemCard } from "../CatalogItemCard";
import { SearchInput } from "../SearchInput";
import { SkeletonItemCard } from "../SkeletonItemCard";
import { SortDropdown } from "../SortDropdown";

const SKELETON_PLACEHOLDER_COUNT = 6;

export interface ItemSelectionViewProps {
  className?: string;
}

export function ItemSelectionView({ className }: ItemSelectionViewProps) {
  const { state, filteredRootItems, selectRootItem, setSearchTerm, setSortKey } =
    useWidget();
  const { isLoading, filters, searchTerm, sortKey, rootHasPricing } = state;

  const hasFilters =
    filters &&
    (filters.selectFilters.length > 0 || filters.rangeFilters.length > 0);

  return (
    <div className={cn("apex-flex apex-flex-col apex-h-full", className)}>
      {/* Toolbar — search + sort */}
      {/* Wraps on phones: search and sort side by side overflowed a 360px
          viewport once the sort control carried range-filter options. */}
      <div className="apex-flex apex-flex-wrap apex-items-center apex-gap-3 apex-px-4 sm:apex-px-6 apex-py-3 apex-border-b apex-border-border apex-bg-background">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search models..."
          aria-label="Search models"
        />
        <SortDropdown
          value={sortKey || "featured"}
          onChange={setSortKey}
          rangeFilters={filters?.rangeFilters ?? []}
        />
      </div>

      {/* Filters */}
      {hasFilters && <FilterBar />}

      {/* Items Grid */}
      <div className="apex-flex-1 apex-overflow-auto apex-p-6">
        {isLoading ? (
          // Skeleton placeholders match the real grid so layout doesn't
          // shift once content loads (#533).
          <div className="apex-grid apex-grid-cols-1 sm:apex-grid-cols-2 lg:apex-grid-cols-3 apex-gap-4">
            {Array.from({ length: SKELETON_PLACEHOLDER_COUNT }).map((_, i) => (
              <SkeletonItemCard key={i} />
            ))}
          </div>
        ) : filteredRootItems.length === 0 ? (
          <div className="apex-flex apex-flex-col apex-items-center apex-justify-center apex-py-12 apex-text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="apex-text-muted-foreground apex-mb-4"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <p className="apex-text-lg apex-font-medium apex-text-foreground apex-mb-1">
              {searchTerm
                ? `No models matching "${searchTerm}"`
                : "No items found"}
            </p>
            <p className="apex-text-sm apex-text-muted-foreground">
              {searchTerm
                ? "Try a different search or clear filters."
                : "Try adjusting your filters to see more results."}
            </p>
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="apex-mt-4 apex-text-sm apex-text-primary hover:apex-underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="apex-grid apex-grid-cols-1 sm:apex-grid-cols-2 lg:apex-grid-cols-3 apex-gap-4">
            {filteredRootItems.map((rootItem, index) => (
              <CatalogItemCard
                key={rootItem.id}
                rootItem={rootItem}
                onSelect={selectRootItem}
                hasPricing={rootHasPricing}
                index={index}
              />
            ))}
          </div>
        )}
      </div>

      {/* Results Count */}
      {!isLoading && filteredRootItems.length > 0 && (
        <div className="apex-border-t apex-border-border apex-px-6 apex-py-2 apex-text-sm apex-text-muted-foreground">
          {filteredRootItems.length === state.rootItems.length
            ? `${filteredRootItems.length} items`
            : `${filteredRootItems.length} of ${state.rootItems.length} items`}
        </div>
      )}
    </div>
  );
}
